import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { PageHero } from "./Services";
import { ArrowLeft } from "lucide-react";

const MD = {
  h1: ({ node, ...p }) => <h2 className="font-heading text-2xl font-bold text-navy mt-8 mb-2" {...p} />,
  h2: ({ node, ...p }) => <h2 className="font-heading text-2xl font-bold text-navy mt-8 mb-2" {...p} />,
  h3: ({ node, ...p }) => <h3 className="font-heading text-xl font-semibold text-navy mt-6 mb-2" {...p} />,
  p: ({ node, ...p }) => <p className="text-lg text-slate-700 leading-relaxed" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-6 space-y-1.5 text-slate-700" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-6 space-y-1.5 text-slate-700" {...p} />,
  li: ({ node, ...p }) => <li className="text-lg" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-semibold text-navy" {...p} />,
  a: ({ node, ...p }) => <a className="text-electric underline" target="_blank" rel="noreferrer" {...p} />,
  blockquote: ({ node, ...p }) => <blockquote className="border-l-4 border-electric pl-4 italic text-slate-600" {...p} />,
};

export function Blog() {
  const { data: posts, isLoading } = useQuery({ queryKey: ["blog"], queryFn: () => api.get("/blog").then(r => r.data.posts) });
  return (
    <div>
      <PageHero label="Blog" title="Tips, guides & insights" subtitle="Practical advice on websites, PC care, and automation for small businesses." />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        {isLoading ? <p className="text-slate-500">Loading…</p> : (
          <div className="grid md:grid-cols-2 gap-5">
            {(posts || []).map((p) => (
              <Link to={`/blog/${p.slug}`} key={p.id} data-testid={`blog-card-${p.slug}`} className="group rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all">
                {p.featuredImage && <div className="aspect-video bg-slate-100 overflow-hidden"><img src={p.featuredImage} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /></div>}
                <div className="p-6">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-electric">{p.category}</span>
                  <h2 className="mt-2 font-heading text-xl font-semibold text-navy group-hover:text-electric transition-colors">{p.title}</h2>
                  <p className="mt-2 text-sm text-slate-600">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { data: posts } = useQuery({ queryKey: ["blog"], queryFn: () => api.get("/blog").then(r => r.data.posts) });
  const post = (posts || []).find(p => p.slug === slug);
  if (!post) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-500">Loading article…</div>;
  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
      <Link to="/blog" className="text-sm text-electric flex items-center gap-1.5 mb-6"><ArrowLeft className="h-4 w-4" /> All articles</Link>
      <span className="text-[10px] font-mono uppercase tracking-wider text-electric">{post.category}</span>
      <h1 className="font-heading text-3xl md:text-4xl font-bold text-navy mt-2">{post.title}</h1>
      <p className="mt-3 text-slate-500 text-sm">By {post.author}</p>
      {post.featuredImage && <img src={post.featuredImage} alt={post.title} className="mt-6 w-full rounded-xl border border-slate-200" />}
      <div className="mt-8 text-slate-700 leading-relaxed space-y-4" data-testid="blog-content">
        <ReactMarkdown components={MD}>{post.content}</ReactMarkdown>
      </div>
    </article>
  );
}
